import { BooLogo } from "@/components/BooLogo";
import { useBooGradientStyle } from "@/hooks/useBooGradientStyle";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { signOut, useSession } from "@/lib/auth-client";
import { useQuery } from "@tanstack/react-query";
import { LogOut } from "lucide-react";
import { Outlet, useNavigate, useRouterState } from "@tanstack/react-router";
import type { CSSProperties, ReactNode } from "react";
import { useEffect } from "react";
import { clearOnboardingPosture } from "@/lib/posture";
import { PreviewProvider } from "./PreviewProvider";
import { usePreview } from "./preview-context";
import { Step1Preview } from "./previews/Step1Preview";
import { Step3Preview } from "./previews/Step3Preview";
import { WelcomePreview } from "./previews/WelcomePreview";
import { CosStepPreview } from "./cos/CosStepPreview";
import { WaitlistScreen } from "./WaitlistScreen";

// Maps the backend's onboarding-state step to a route path. The layout
// uses this to decide where to send the user. Source of truth is the
// /api/onboarding/state endpoint — frontend never decides on its own.
// NOTE: `waitlist` is intentionally absent — GatedShell renders the
// standalone WaitlistScreen in place (no route hop), which avoids a flash of
// the onboarding chrome before the waitlist screen appears.
const STEP_TO_PATH: Record<string, string> = {
  welcome: "/onboarding/welcome",
  organization: "/onboarding/organization",
  provisioning: "/onboarding/provisioning",
  connect: "/onboarding/connect",
};

// Display-only — actual gating lives on the backend (/api/onboarding/state).
// Sign-up users who provide a name up-front (email-OTP flow) skip the
// welcome screen entirely; the Stepper component below filters the "You"
// segment out for them so the visible stepper matches the steps they'll
// actually walk through.
const STEPS = [
  { path: "/onboarding/welcome", label: "You" },
  { path: "/onboarding/organization", label: "Workspace" },
  { path: "/onboarding/provisioning", label: "Setup" },
  { path: "/onboarding/connect", label: "Connect" },
] as const;

function ActiveBar() {
  const gradientStyle = useBooGradientStyle();
  const style: CSSProperties = {
    ...gradientStyle,
    animation: "boo-logo 4s ease-in-out infinite",
  };
  return <div className="absolute inset-0" style={style} />;
}

function Stepper() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { data: session } = useSession();
  // Email-OTP signup collects the name up-front, so most users skip the
  // welcome screen. Hide the "You" segment for them — a permanently-
  // grayed-out dot they never interact with would just be misleading
  // clutter. GatedShell's redirect still uses the full STEP_TO_PATH map,
  // so a no-name URL-jumper is still pushed to /welcome correctly.
  const hasName = !!session?.user?.name?.trim();
  const visibleSteps = hasName ? STEPS.slice(1) : STEPS;
  const currentIndex = visibleSteps.findIndex((s) => pathname.startsWith(s.path));

  return (
    <div className="flex gap-2">
      {visibleSteps.map((step, i) => {
        const completed = i < currentIndex;
        const active = i === currentIndex;
        return (
          <div
            key={step.path}
            className={`relative h-1.5 flex-1 rounded-full overflow-hidden ${
              completed ? "bg-foreground" : active ? "" : "bg-border"
            }`}
          >
            {active && <ActiveBar />}
          </div>
        );
      })}
    </div>
  );
}

function StepPreview() {
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const { posture } = usePreview();

  // The chief-of-staff branch has its own preview set: one person, a DM,
  // a morning briefing, instead of the team channel scenes.
  if (posture === "chief-of-staff") return <CosStepPreview pathname={pathname} />;

  if (pathname.startsWith("/onboarding/welcome")) {
    return <WelcomePreview />;
  }
  // Provisioning + Connect share the Step3Preview illustration — the catalog
  // is no longer a side pane; it opens as a full-screen takeover from the
  // pitch's "Browse integrations" button.
  if (
    pathname.startsWith("/onboarding/connect") ||
    pathname.startsWith("/onboarding/provisioning")
  ) {
    return <Step3Preview />;
  }
  return <Step1Preview />;
}


// Gate the onboarding UI on (a) the backend's authoritative state and
// (b) the Haiku suggestions call. The state check is the auth layer the
// user asked for: regardless of what URL they type, we redirect them to
// the step the backend says they're on. Frontend never decides on its
// own.
function GatedShell({ children }: { children: ReactNode }) {
  const { applySuggestions, posture, setPosture } = usePreview();
  const pathname = useRouterState({ select: (s) => s.location.pathname });
  const navigate = useNavigate();

  // 1. Authoritative onboarding state from the backend.
  const stateQuery = useQuery({
    queryKey: ["onboarding-state"],
    queryFn: api.onboarding.state,
    staleTime: 0,
    // Poll while waitlisted so the screen auto-advances the moment an FDE
    // approves (step flips waitlist → organization).
    refetchInterval: (q) => (q.state.data?.step === "waitlist" ? 3000 : false),
  });

  // Redirect to the correct step whenever state changes or path
  // disagrees with state. Already-completed users are bounced to the
  // dashboard so URL-jumping into /onboarding/* can't trap them here.
  // The "let them sit on /organization while backend says connect"
  // override is no longer needed — backend now returns "organization"
  // until the user submits the workspace form, and "provisioning"
  // while the container settles, so the step value always matches
  // where they should be.
  useEffect(() => {
    if (!stateQuery.data) return;
    const { step } = stateQuery.data;
    if (step === "complete") {
      // The entry-path intent has done its job once onboarding is over.
      clearOnboardingPosture();
      navigate({ to: "/", replace: true });
      return;
    }
    const expected = STEP_TO_PATH[step];
    if (!expected) return;
    // Ready-state exception: once provisioning finishes, an engaged user is
    // allowed to stay on /provisioning connecting integrations; the step
    // itself shows the ready state and owns the Continue handoff.
    if (step === "connect" && pathname.startsWith("/onboarding/provisioning")) return;
    if (!pathname.startsWith(expected)) {
      navigate({ to: expected, replace: true });
    }
  }, [stateQuery.data, pathname, navigate]);

  // Once the container row exists its posture is canonical; the stored
  // entry-path intent only covers the steps before that.
  const statePosture = stateQuery.data?.posture;
  useEffect(() => {
    if (statePosture && statePosture !== posture) setPosture(statePosture);
  }, [statePosture, posture, setPosture]);

  const onOrganizationStep = pathname.startsWith("/onboarding/organization");

  const suggestionsQuery = useQuery({
    queryKey: ["onboarding-suggestions"],
    queryFn: () => api.onboarding.suggestions(),
    staleTime: Infinity,
    retry: false,
    enabled: onOrganizationStep,
  });

  useEffect(() => {
    if (suggestionsQuery.data) applySuggestions(suggestionsQuery.data);
  }, [suggestionsQuery.data, applySuggestions]);

  // 2. Show loader while we fetch initial state OR (on the org step)
  // wait for Haiku suggestions. No flash of wrong UI either way.
  const stateLoading = stateQuery.isPending && !stateQuery.isFetched;
  const suggestionsLoading =
    onOrganizationStep && suggestionsQuery.isPending && !suggestionsQuery.isFetched;
  if (stateLoading || suggestionsLoading) return <CenteredBooLoader />;
  if (stateQuery.isError) return <CenteredBooError error={stateQuery.error} />;

  // Waitlisted signup: render the standalone waitlist screen in place —
  // no onboarding chrome, and no /onboarding → /waitlist route hop (which
  // caused a momentary flash of the onboarding shell). Polling above makes it
  // auto-advance once approved.
  if (stateQuery.data?.step === "waitlist") return <WaitlistScreen />;

  // 3. Don't render the (possibly mismatched) child if state and path
  // disagree — the redirect effect above will fix it shortly. Same
  // ready-state exception as the effect: provisioning is a valid place to
  // sit while the backend already says "connect".
  const expected = stateQuery.data ? STEP_TO_PATH[stateQuery.data.step] : undefined;
  const readyOnProvisioning =
    stateQuery.data?.step === "connect" &&
    pathname.startsWith("/onboarding/provisioning");
  if (
    stateQuery.data &&
    stateQuery.data.step !== "complete" &&
    expected &&
    !pathname.startsWith(expected) &&
    !readyOnProvisioning
  ) {
    return <CenteredBooLoader />;
  }

  return <>{children}</>;
}

function CenteredBooError({ error }: { error: unknown }) {
  const message = error instanceof Error ? error.message : "Something went wrong";
  return (
    <div className="h-screen flex flex-col items-center justify-center bg-background gap-4">
      <BooLogo className="size-16 rounded-2xl opacity-50" />
      <p className="text-sm text-destructive">{message}</p>
    </div>
  );
}

function CenteredBooLoader() {
  return (
    <div className="h-screen flex items-center justify-center bg-background">
      <div className="relative">
        <span
          className="boo-pulse absolute -inset-8 rounded-full"
          style={{
            background:
              "radial-gradient(closest-side, rgba(127, 119, 221, 0.4), transparent 70%)",
          }}
        />
        <BooLogo className="relative size-16 rounded-2xl" />
      </div>
    </div>
  );
}

// Escape hatch during onboarding — e.g. wrong account. Hard-navigates to
// /login on success so no stale session state lingers.
function LogoutButton() {
  return (
    <Button
      type="button"
      variant="outline"
      size="sm"
      onClick={() =>
        signOut({ fetchOptions: { onSuccess: () => window.location.assign("/login") } })
      }
      className="fixed bottom-6 right-6 z-20 bg-background shadow-sm"
    >
      <LogOut />
      Log out
    </Button>
  );
}

export function OnboardingLayout() {
  return (
    <PreviewProvider>
      <GatedShell>
        <div className="min-h-screen p-2 md:p-4 flex gap-4 bg-background">
          <div className="w-full md:basis-1/2 flex flex-col items-center p-4 pt-8 md:p-8 md:pt-8">
            <div className="w-full max-w-sm flex flex-col gap-6">
              <Stepper />
              <div className="flex-1 min-h-0 flex flex-col">
                <Outlet />
              </div>
            </div>
          </div>
          {/* min-w-0 lets the provisioning catalog pane shrink to its half
              instead of growing to its content's intrinsic width. */}
          <div className="hidden md:block md:basis-1/2 min-w-0">
            <StepPreview />
          </div>
        </div>
        <LogoutButton />
      </GatedShell>
    </PreviewProvider>
  );
}
